import React, { useState, useEffect, useCallback } from "react";
import ebay from "../services/ebay";
import ebayDescription from "../services/ebay-description";

const MARKETPLACES = [
  { id: "EBAY_US", label: "United States", currency: "USD" },
  { id: "EBAY_CA", label: "Canada", currency: "CAD" },
  { id: "EBAY_GB", label: "United Kingdom", currency: "GBP" },
  { id: "EBAY_AU", label: "Australia", currency: "AUD" },
  { id: "EBAY_DE", label: "Germany", currency: "EUR" },
];

const EMPTY_ADDRESS = {
  addressLine1: "",
  city: "",
  stateOrProvince: "",
  postalCode: "",
  country: "US",
};

/**
 * eBay Integration settings.
 *
 * Holds the OAuth credentials, the business policies and inventory location an
 * offer needs before it can publish, and the HTML description template.
 */
function EbaySettings() {
  const [creds, setCreds] = useState(() => ebay.getCredentials());
  const [showSecret, setShowSecret] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message }
  const [isTesting, setIsTesting] = useState(false);
  const [isLoadingAccount, setIsLoadingAccount] = useState(false);
  const [authCodeInput, setAuthCodeInput] = useState("");
  const [isExchanging, setIsExchanging] = useState(false);

  const [policies, setPolicies] = useState({
    fulfillment: [],
    payment: [],
    returns: [],
  });
  const [locations, setLocations] = useState([]);
  const [showNewLocation, setShowNewLocation] = useState(false);
  const [newLocationKey, setNewLocationKey] = useState("default");
  const [newLocationAddress, setNewLocationAddress] = useState(EMPTY_ADDRESS);

  const [presentation, setPresentation] = useState(() =>
    ebayDescription.getPresentation(),
  );
  const [presentationSaved, setPresentationSaved] = useState(false);
  const [freeShipping, setFreeShipping] = useState(false);
  const [freeShippingBusy, setFreeShippingBusy] = useState(false);
  const [template, setTemplate] = useState(() => ebayDescription.getTemplate());
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  const envMismatch = ebay.getEnvironmentMismatch(creds);
  const configured = ebay.isConfigured();
  const ready = ebay.isReadyToList();
  const missing = ebay.getMissingRequirements();

  const updateField = (field, value) => {
    setCreds((prev) => ({ ...prev, [field]: value }));
    setStatus(null);
  };

  const handleSaveCredentials = () => {
    if (!creds.clientId || !creds.clientSecret || !creds.refreshToken) {
      setStatus({
        type: "error",
        message: "Client ID, Client Secret and Refresh Token are all required",
      });
      return;
    }

    ebay.saveCredentials(creds);
    setCreds(ebay.getCredentials());
    setStatus({ type: "success", message: "Credentials saved" });
  };

  /** Load the seller's policies and locations so they can be picked from a list. */
  const loadAccountConfig = useCallback(async () => {
    setIsLoadingAccount(true);
    setStatus(null);
    try {
      const [fetchedPolicies, fetchedLocations] = await Promise.all([
        ebay.getPolicies(),
        ebay.getLocations().catch(() => []),
      ]);
      setPolicies(fetchedPolicies);
      setLocations(fetchedLocations);
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsLoadingAccount(false);
    }
  }, []);

  // Reflect the selected shipping policy's current free-shipping setting.
  useEffect(() => {
    if (!creds.fulfillmentPolicyId) return;
    let cancelled = false;
    ebay
      .isFreeShipping(creds.fulfillmentPolicyId)
      .then((value) => {
        if (!cancelled) setFreeShipping(value);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [creds.fulfillmentPolicyId]);

  const handleToggleFreeShipping = async (enabled) => {
    setFreeShippingBusy(true);
    setStatus(null);
    try {
      await ebay.setFreeShipping(creds.fulfillmentPolicyId, enabled);
      setFreeShipping(enabled);
      setStatus({
        type: "success",
        message: enabled
          ? "Free shipping enabled on your shipping policy."
          : "Free shipping disabled; buyers pay the flat rate.",
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setFreeShippingBusy(false);
    }
  };

  // Pull the account config once credentials are in place.
  useEffect(() => {
    if (configured) loadAccountConfig();
  }, [configured, loadAccountConfig]);

  /** Validate the app keys alone — no user consent involved. */
  const handleTestCredentials = async () => {
    setIsTesting(true);
    setStatus(null);
    try {
      await ebay.testCredentials(creds);
      setStatus({
        type: "success",
        message:
          "App keys are valid. Next, authorise your seller account below.",
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleOpenConsent = () => {
    try {
      ebay.saveCredentials(creds);
      setCreds(ebay.getCredentials());
      window.open(ebay.getConsentUrl(creds), "_blank", "noopener");
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    }
  };

  const handleExchangeCode = async () => {
    setIsExchanging(true);
    setStatus(null);
    try {
      await ebay.exchangeAuthCode(authCodeInput, creds);
      setCreds(ebay.getCredentials());
      setAuthCodeInput("");
      await loadAccountConfig();
      setStatus({
        type: "success",
        message: "Refresh token saved. eBay is connected.",
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsExchanging(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setStatus(null);
    try {
      await ebay.getAccessToken({ force: true });
      await loadAccountConfig();
      setStatus({ type: "success", message: "Connected to eBay successfully" });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  /**
   * One-click bootstrap: opt into policy management, create the three
   * required policies, and create an inventory location if none exists.
   */
  const handleCreateDefaults = async () => {
    setIsLoadingAccount(true);
    setStatus(null);
    try {
      const result = await ebay.createDefaultPolicies();

      // A location needs a postal code, so only auto-create when we have one.
      let locationNote = "";
      const locations = await ebay.getLocations().catch(() => []);
      if (locations.length === 0) {
        if (newLocationAddress.postalCode) {
          await ebay.createLocation(
            newLocationKey || "default",
            newLocationAddress,
          );
          locationNote = ", inventory location";
        } else {
          locationNote = " — add a postal code below to create the location";
        }
      }

      setCreds(ebay.getCredentials());
      await loadAccountConfig();

      if (result.errors.length > 0) {
        setStatus({
          type: "error",
          message: `Some policies could not be created — ${result.errors.join("; ")}`,
        });
      } else {
        const parts = [];
        if (result.created.length)
          parts.push(`created ${result.created.join(", ")}`);
        if (result.skipped.length)
          parts.push(`kept existing ${result.skipped.join(", ")}`);
        setStatus({
          type: "success",
          message: `${parts.join("; ") || "Nothing to do"}${locationNote}.`,
        });
      }
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsLoadingAccount(false);
    }
  };

  const handleSelectPolicy = (field, value) => {
    ebay.saveCredentials({ [field]: value });
    setCreds(ebay.getCredentials());
  };

  const handleCreateLocation = async () => {
    if (!newLocationKey || !newLocationAddress.postalCode) {
      setStatus({
        type: "error",
        message: "A location key and postal code are required",
      });
      return;
    }

    setIsLoadingAccount(true);
    setStatus(null);
    try {
      await ebay.createLocation(newLocationKey, newLocationAddress);
      ebay.saveCredentials({ merchantLocationKey: newLocationKey });
      setCreds(ebay.getCredentials());
      setShowNewLocation(false);
      await loadAccountConfig();
      setStatus({
        type: "success",
        message: `Location "${newLocationKey}" created`,
      });
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setIsLoadingAccount(false);
    }
  };

  const handleDisconnect = () => {
    if (!window.confirm("Disconnect eBay and clear stored credentials?"))
      return;
    ebay.clearCredentials();
    setCreds(ebay.getCredentials());
    setPolicies({ fulfillment: [], payment: [], returns: [] });
    setLocations([]);
    setStatus(null);
  };

  const updatePresentation = (field, value) => {
    setPresentation((prev) => ({ ...prev, [field]: value }));
    setPresentationSaved(false);
  };

  const handleSavePresentation = () => {
    ebayDescription.savePresentation(presentation);
    ebay.scheduleServerSync();
    setPresentationSaved(true);
    setTimeout(() => setPresentationSaved(false), 2000);
  };

  const handleSaveTemplate = () => {
    ebayDescription.saveTemplate(template);
    ebay.scheduleServerSync();
    setTemplateSaved(true);
    setTimeout(() => setTemplateSaved(false), 2000);
  };

  const handleResetTemplate = () => {
    ebayDescription.resetTemplate();
    setTemplate(ebayDescription.DEFAULT_TEMPLATE);
  };

  const handleMarketplaceChange = (marketplaceId) => {
    const match = MARKETPLACES.find((m) => m.id === marketplaceId);
    setCreds((prev) => ({
      ...prev,
      marketplaceId,
      currency: match?.currency || prev.currency,
    }));
  };

  const renderPolicySelect = (label, field, options, valueKey, nameKey) => (
    <div>
      <label>{label}</label>
      <select
        value={creds[field] || ""}
        onChange={(e) => handleSelectPolicy(field, e.target.value)}
        disabled={options.length === 0}
      >
        <option value="">
          {options.length === 0
            ? "None found — check your eBay account"
            : "Select…"}
        </option>
        {options.map((option) => (
          <option key={option[valueKey]} value={option[valueKey]}>
            {option[nameKey]}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
      <h3 style={{ marginBottom: "var(--spacing-md)" }}>eBay Integration</h3>
      <p
        className="text-secondary"
        style={{ marginBottom: "var(--spacing-md)" }}
      >
        Publish products to eBay through the official Sell API. Products synced
        from Amazon use their Amazon title and photos automatically.
      </p>

      {status && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderRadius: "var(--radius-sm)",
            marginBottom: "var(--spacing-md)",
            background:
              status.type === "success"
                ? "var(--success-bg, rgba(16, 185, 129, 0.1))"
                : "var(--danger-bg, rgba(239, 68, 68, 0.1))",
            color:
              status.type === "success"
                ? "var(--success, #10b981)"
                : "var(--danger, #ef4444)",
          }}
        >
          {status.message}
        </div>
      )}

      {envMismatch && (
        <div
          style={{
            padding: "var(--spacing-sm) var(--spacing-md)",
            borderRadius: "var(--radius-sm)",
            marginBottom: "var(--spacing-md)",
            background: "var(--warning-bg, rgba(245, 158, 11, 0.1))",
            border: "1px solid var(--warning, #f59e0b)",
            color: "var(--warning, #f59e0b)",
            fontSize: "0.875rem",
          }}
        >
          {envMismatch}
        </div>
      )}

      {configured && (
        <div
          style={{
            padding: "var(--spacing-md)",
            background: ready
              ? "var(--success-bg, rgba(16, 185, 129, 0.1))"
              : "var(--warning-bg, rgba(245, 158, 11, 0.1))",
            border: `1px solid ${ready ? "var(--success, #10b981)" : "var(--warning, #f59e0b)"}`,
            borderRadius: "var(--radius-sm)",
            marginBottom: "var(--spacing-md)",
            fontSize: "0.875rem",
          }}
        >
          {ready ? (
            <span style={{ color: "var(--success, #10b981)", fontWeight: 500 }}>
              ✓ eBay is ready to publish listings
            </span>
          ) : (
            <>
              <strong style={{ color: "var(--warning, #f59e0b)" }}>
                Setup incomplete
              </strong>
              <div style={{ marginTop: "4px" }}>
                Still needed: {missing.join(", ")}
              </div>
            </>
          )}
        </div>
      )}

      {/* OAuth credentials */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--spacing-sm)",
        }}
      >
        <div>
          <label>Environment</label>
          <select
            value={creds.environment}
            onChange={(e) => updateField("environment", e.target.value)}
          >
            <option value="production">Production</option>
            <option value="sandbox">Sandbox</option>
          </select>
        </div>
        <div>
          <label>Marketplace</label>
          <select
            value={creds.marketplaceId}
            onChange={(e) => handleMarketplaceChange(e.target.value)}
          >
            {MARKETPLACES.map((market) => (
              <option key={market.id} value={market.id}>
                {market.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label style={{ marginTop: "var(--spacing-md)" }}>
        Client ID (App ID)
      </label>
      <input
        type="text"
        value={creds.clientId || ""}
        onChange={(e) => updateField("clientId", e.target.value)}
        placeholder="YourApp-FrugalSt-PRD-a1b2c3d4e-5f6a7b8c"
        style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
      />

      <label style={{ marginTop: "var(--spacing-md)" }}>
        Client Secret (Cert ID)
      </label>
      <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
        <input
          type={showSecret ? "text" : "password"}
          value={creds.clientSecret || ""}
          onChange={(e) => updateField("clientSecret", e.target.value)}
          placeholder="PRD-a1b2c3d4e5f6-7a8b-9c0d-1e2f"
          style={{ fontFamily: "monospace", fontSize: "0.8rem", flex: 1 }}
        />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setShowSecret((prev) => !prev)}
        >
          {showSecret ? "Hide" : "Show"}
        </button>
      </div>

      <label style={{ marginTop: "var(--spacing-md)" }}>
        User Refresh Token
      </label>
      <textarea
        value={creds.refreshToken || ""}
        onChange={(e) => updateField("refreshToken", e.target.value)}
        placeholder="v^1.1#i^1#f^0#r^1#I^3#p^3#t^Ul4..."
        rows={3}
        style={{ fontFamily: "monospace", fontSize: "0.75rem" }}
      />
      <small style={{ color: "var(--text-muted)" }}>
        Obtained through the consent flow below — you shouldn't need to paste
        this by hand.
      </small>

      <label style={{ marginTop: "var(--spacing-md)" }}>
        RuName (Redirect URL name)
      </label>
      <input
        type="text"
        value={creds.ruName || ""}
        onChange={(e) => updateField("ruName", e.target.value)}
        placeholder="Shinn_Lok-ShinnLok-Sandbo-abcdefgh"
        style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
      />
      <small style={{ color: "var(--text-muted)" }}>
        From your eBay developer account under "User tokens" &rarr; "Get a Token
        from eBay via Your Application". This is the RuName, not the callback
        URL.
      </small>

      {/* Consent flow — the only way to obtain a user refresh token */}
      <div
        style={{
          marginTop: "var(--spacing-md)",
          padding: "var(--spacing-md)",
          background: "var(--bg-secondary)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <strong style={{ fontSize: "0.9rem" }}>
          Authorise your seller account
        </strong>
        <ol
          style={{
            margin: "var(--spacing-xs) 0 var(--spacing-sm) var(--spacing-md)",
            padding: 0,
            fontSize: "0.85rem",
            color: "var(--text-secondary)",
          }}
        >
          <li>Save your credentials and RuName above.</li>
          <li>
            Open the eBay consent page and sign in as the selling account.
          </li>
          <li>
            After approving, eBay redirects to your RuName's callback URL. Copy
            that whole URL from the address bar and paste it below.
          </li>
        </ol>
        <button
          type="button"
          onClick={handleOpenConsent}
          className="btn btn-secondary btn-sm"
          disabled={!creds.clientId || !creds.ruName}
        >
          Open eBay Consent Page
        </button>

        <label style={{ marginTop: "var(--spacing-md)" }}>
          Redirect URL or authorization code
        </label>
        <textarea
          value={authCodeInput}
          onChange={(e) => setAuthCodeInput(e.target.value)}
          rows={3}
          placeholder="https://auth.sandbox.ebay.com/oauth2/…?code=v%5E1.1%23i%5E1%23…"
          style={{ fontFamily: "monospace", fontSize: "0.7rem" }}
        />
        <button
          type="button"
          onClick={handleExchangeCode}
          className="btn btn-primary btn-sm"
          disabled={!authCodeInput || isExchanging}
          style={{ marginTop: "var(--spacing-xs)" }}
        >
          {isExchanging ? "Exchanging…" : "Get Refresh Token"}
        </button>
        <div
          style={{
            marginTop: "var(--spacing-xs)",
            fontSize: "0.8rem",
            color: "var(--text-muted)",
          }}
        >
          The code expires within minutes — paste it right after approving.
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "var(--spacing-sm)",
          marginTop: "var(--spacing-md)",
          flexWrap: "wrap",
        }}
      >
        <button onClick={handleSaveCredentials} className="btn btn-primary">
          Save Credentials
        </button>
        <button
          onClick={handleTestCredentials}
          className="btn btn-secondary"
          disabled={!creds.clientId || !creds.clientSecret || isTesting}
        >
          {isTesting ? "Testing…" : "Test App Keys"}
        </button>
        <button
          onClick={handleTestConnection}
          className="btn btn-secondary"
          disabled={!configured || isTesting}
        >
          Test Connection
        </button>
        {configured && (
          <button onClick={handleDisconnect} className="btn btn-danger btn-sm">
            Disconnect
          </button>
        )}
      </div>

      {/* Business policies and location — required before an offer can publish */}
      {configured && (
        <div style={{ marginTop: "var(--spacing-lg)" }}>
          <h4 style={{ marginBottom: "var(--spacing-sm)" }}>
            Listing Defaults{" "}
            {isLoadingAccount && (
              <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                — loading…
              </span>
            )}
          </h4>
          <p
            className="text-secondary"
            style={{ fontSize: "0.875rem", marginBottom: "var(--spacing-md)" }}
          >
            eBay applies these business policies to every listing. Create them
            in eBay Seller Hub if the lists come back empty.
          </p>

          {!ready && (
            <div
              style={{
                marginBottom: "var(--spacing-md)",
                padding: "var(--spacing-md)",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <div
                style={{
                  fontSize: "0.875rem",
                  marginBottom: "var(--spacing-sm)",
                }}
              >
                No policies yet? This opts your account into eBay's business
                policy management and creates a basic shipping, payment and
                return policy. You can edit them in Seller Hub afterwards.
              </div>
              <button
                type="button"
                onClick={handleCreateDefaults}
                className="btn btn-primary btn-sm"
                disabled={isLoadingAccount}
              >
                {isLoadingAccount ? "Working…" : "Create Default Policies"}
              </button>
            </div>
          )}

          <div style={{ display: "grid", gap: "var(--spacing-sm)" }}>
            {renderPolicySelect(
              "Shipping Policy",
              "fulfillmentPolicyId",
              policies.fulfillment,
              "fulfillmentPolicyId",
              "name",
            )}
            {renderPolicySelect(
              "Payment Policy",
              "paymentPolicyId",
              policies.payment,
              "paymentPolicyId",
              "name",
            )}
            {renderPolicySelect(
              "Return Policy",
              "returnPolicyId",
              policies.returns,
              "returnPolicyId",
              "name",
            )}

            {creds.fulfillmentPolicyId && (
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--spacing-sm)",
                  margin: 0,
                  cursor: freeShippingBusy ? "wait" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={freeShipping}
                  disabled={freeShippingBusy}
                  onChange={(e) => handleToggleFreeShipping(e.target.checked)}
                  style={{ width: "auto", margin: 0 }}
                />
                Offer free shipping
                <span
                  style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}
                >
                  {freeShippingBusy
                    ? "saving…"
                    : "(applies to all eBay listings)"}
                </span>
              </label>
            )}

            <div>
              <label>Inventory Location</label>
              <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
                <select
                  value={creds.merchantLocationKey || ""}
                  onChange={(e) =>
                    handleSelectPolicy("merchantLocationKey", e.target.value)
                  }
                  style={{ flex: 1 }}
                >
                  <option value="">
                    {locations.length === 0
                      ? "None found — create one"
                      : "Select…"}
                  </option>
                  {locations.map((location) => (
                    <option
                      key={location.merchantLocationKey}
                      value={location.merchantLocationKey}
                    >
                      {location.name || location.merchantLocationKey}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowNewLocation((prev) => !prev)}
                >
                  {showNewLocation ? "Cancel" : "New"}
                </button>
              </div>
            </div>
          </div>

          {showNewLocation && (
            <div
              style={{
                marginTop: "var(--spacing-md)",
                padding: "var(--spacing-md)",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <label>Location Key</label>
              <input
                type="text"
                value={newLocationKey}
                onChange={(e) => setNewLocationKey(e.target.value)}
                placeholder="default"
              />
              <label style={{ marginTop: "var(--spacing-sm)" }}>Address</label>
              <input
                type="text"
                value={newLocationAddress.addressLine1}
                onChange={(e) =>
                  setNewLocationAddress({
                    ...newLocationAddress,
                    addressLine1: e.target.value,
                  })
                }
                placeholder="Street address"
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: "var(--spacing-sm)",
                  marginTop: "var(--spacing-sm)",
                }}
              >
                <input
                  type="text"
                  value={newLocationAddress.city}
                  onChange={(e) =>
                    setNewLocationAddress({
                      ...newLocationAddress,
                      city: e.target.value,
                    })
                  }
                  placeholder="City"
                />
                <input
                  type="text"
                  value={newLocationAddress.stateOrProvince}
                  onChange={(e) =>
                    setNewLocationAddress({
                      ...newLocationAddress,
                      stateOrProvince: e.target.value,
                    })
                  }
                  placeholder="State"
                />
                <input
                  type="text"
                  value={newLocationAddress.postalCode}
                  onChange={(e) =>
                    setNewLocationAddress({
                      ...newLocationAddress,
                      postalCode: e.target.value,
                    })
                  }
                  placeholder="ZIP"
                />
              </div>
              <button
                onClick={handleCreateLocation}
                className="btn btn-primary btn-sm"
                style={{ marginTop: "var(--spacing-sm)" }}
                disabled={isLoadingAccount}
              >
                Create Location
              </button>
            </div>
          )}
        </div>
      )}

      {/* Presentation defaults shared by every description */}
      <div style={{ marginTop: "var(--spacing-lg)" }}>
        <h4 style={{ marginBottom: "var(--spacing-xs)" }}>
          Listing Presentation
        </h4>
        <p
          className="text-secondary"
          style={{ fontSize: "0.875rem", marginBottom: "var(--spacing-md)" }}
        >
          Applied to every eBay description. Leave the banner blank to omit it.
        </p>

        <label>Banner Image URL</label>
        <input
          type="url"
          value={presentation.bannerUrl}
          onChange={(e) => updatePresentation("bannerUrl", e.target.value)}
          placeholder="https://example.com/assets/store-banner.jpg"
        />
        <small style={{ color: "var(--text-muted)" }}>
          Must be HTTPS — eBay blocks images served over plain HTTP.
        </small>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 2fr",
            gap: "var(--spacing-sm)",
            marginTop: "var(--spacing-md)",
          }}
        >
          <div>
            <label>Handling Days</label>
            <input
              type="text"
              value={presentation.handlingDays}
              onChange={(e) =>
                updatePresentation("handlingDays", e.target.value)
              }
              placeholder="1"
            />
          </div>
          <div>
            <label>Shipping Service</label>
            <input
              type="text"
              value={presentation.shippingService}
              onChange={(e) =>
                updatePresentation("shippingService", e.target.value)
              }
              placeholder="USPS Ground Advantage with tracking"
            />
          </div>
        </div>

        <label style={{ marginTop: "var(--spacing-md)" }}>Return Terms</label>
        <input
          type="text"
          value={presentation.returnTerms}
          onChange={(e) => updatePresentation("returnTerms", e.target.value)}
          placeholder="30-day returns accepted. Buyer pays return shipping."
        />
        <small style={{ color: "var(--text-muted)" }}>
          Keep this consistent with the return policy selected above — eBay
          enforces the policy, not the description.
        </small>

        <label style={{ marginTop: "var(--spacing-md)" }}>
          Packaging Notes (one per line)
        </label>
        <textarea
          value={presentation.packagingLines}
          onChange={(e) => updatePresentation("packagingLines", e.target.value)}
          rows={3}
          placeholder={"Carefully packaged to protect the item in transit"}
        />

        <button
          onClick={handleSavePresentation}
          className="btn btn-primary btn-sm"
          style={{ marginTop: "var(--spacing-sm)" }}
        >
          {presentationSaved ? "Saved ✓" : "Save Presentation"}
        </button>
      </div>

      {/* Description template */}
      <div style={{ marginTop: "var(--spacing-lg)" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h4 style={{ margin: 0 }}>Description Template</h4>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setShowTemplate((prev) => !prev)}
          >
            {showTemplate ? "Hide" : "Edit"}
          </button>
        </div>
        <p
          className="text-secondary"
          style={{ fontSize: "0.875rem", marginTop: "var(--spacing-xs)" }}
        >
          The HTML wrapper for every eBay description.
          {ebayDescription.hasCustomTemplate()
            ? " Using your custom template."
            : " Using the default template."}
        </p>

        {showTemplate && (
          <>
            <textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={16}
              style={{ fontFamily: "monospace", fontSize: "0.75rem" }}
            />
            <div
              style={{
                marginTop: "var(--spacing-sm)",
                padding: "var(--spacing-md)",
                background: "var(--bg-secondary)",
                borderRadius: "var(--radius-md)",
                fontSize: "0.8rem",
              }}
            >
              <strong>Available tokens:</strong>
              <ul
                style={{
                  margin: "var(--spacing-xs) 0 0 var(--spacing-md)",
                  padding: 0,
                }}
              >
                {ebayDescription.TEMPLATE_TOKENS.map((entry) => (
                  <li key={entry.token}>
                    <code>{entry.token}</code> — {entry.description}
                  </li>
                ))}
              </ul>
            </div>
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-sm)",
                marginTop: "var(--spacing-sm)",
              }}
            >
              <button
                onClick={handleSaveTemplate}
                className="btn btn-primary btn-sm"
              >
                {templateSaved ? "Saved ✓" : "Save Template"}
              </button>
              <button
                onClick={handleResetTemplate}
                className="btn btn-secondary btn-sm"
              >
                Reset to Default
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EbaySettings;
